import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { withRouter, type RouteComponentProps } from "react-router-dom";
import { type Dispatch, type UnknownAction } from "redux";

import { listCountryInfoHubDashboard, listCountryInfoHubSubjects } from "../../../../actions";
import { AddNewContentButton, onClickAddNewContent } from "../AddNewContentButton";
import { Dashboard } from "../Dashboard";
import { Filters, type SubjectFilterValues } from "../Filters";
import { SubjectsDataTable } from "../SubjectsDataTable";
import { buildSubjectParams, DEFAULT_SUBJECT_FILTERS } from "../../utils";
import type {
  EntityId,
  InfoHubDashboardData,
  InfoHubSubjectRow,
  SubjectActions,
  SubjectTopic,
} from "../../types";

interface CountryObject {
  id: EntityId;
  name: string;
}

interface OwnProps extends RouteComponentProps {
  country: CountryObject;
  topics: SubjectTopic[];
  types: { label: string; value: string }[];
  setViewModalOptions: (options: { isOpen: boolean; id: EntityId }) => void;
  setConfirmModalOptions: (options: { isOpen: boolean; id: EntityId; type: string }) => void;
}

interface StateProps {
  infoHubSubjects: InfoHubSubjectRow[];
  isFetching: boolean;
  dashboardData?: InfoHubDashboardData;
  isDashboardFetching: boolean;
}

interface DispatchProps {
  listCountryInfoHubSubjects: (filters: Record<string, unknown>) => void;
  listCountryInfoHubDashboard: (filters: Record<string, unknown>) => void;
}

type SelectedCountryTableProps = OwnProps & StateProps & DispatchProps;

export const SelectedCountryTable = ({
  country,
  topics,
  types,
  history,
  setViewModalOptions,
  setConfirmModalOptions,
  infoHubSubjects,
  isFetching,
  dashboardData,
  isDashboardFetching,
  listCountryInfoHubSubjects,
  listCountryInfoHubDashboard,
}: SelectedCountryTableProps) => {
  const [filters, setFilters] = useState<SubjectFilterValues>(DEFAULT_SUBJECT_FILTERS);

  useEffect(() => {
    listCountryInfoHubDashboard({ country: country.id });
  }, []);

  useEffect(() => {
    listCountryInfoHubSubjects(buildSubjectParams(filters, { country: country.id }));
  }, [filters]);

  const actions: SubjectActions = useMemo(
    () => ({
      view: id => setViewModalOptions({ isOpen: true, id }),
      edit: id => history.push(`infohub/newContent/${id}`),
      copy: id => setConfirmModalOptions({ isOpen: true, id, type: "copy" }),
      publish: id => setViewModalOptions({ isOpen: true, id }),
    }),
    [history, setViewModalOptions, setConfirmModalOptions]
  );

  const topicOptions = useMemo(
    () => topics.map(topic => ({ value: topic.id, label: topic.name })),
    [topics]
  );

  return (
    <>
      <Dashboard
        data={dashboardData}
        isFetching={isDashboardFetching}
        onNewMessage={contentType => onClickAddNewContent(history, contentType)}
      />
      <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
        <Filters
          filters={filters}
          onChange={patch => setFilters(current => ({ ...current, ...patch }))}
          onClear={() => setFilters(DEFAULT_SUBJECT_FILTERS)}
          types={types}
          topics={topicOptions}
        />
        <AddNewContentButton history={history} />
      </div>
      <SubjectsDataTable data={infoHubSubjects} isFetching={isFetching} actions={actions} />
    </>
  );
};

interface RequestState<T> {
  isFetching: boolean;
  data: T;
}

interface RootState {
  listCountryInfoHubSubjects: RequestState<{ results?: InfoHubSubjectRow[] }>;
  listCountryInfoHubDashboard: RequestState<InfoHubDashboardData>;
}

export const mapStateToProps = (state: RootState): StateProps => ({
  infoHubSubjects: state.listCountryInfoHubSubjects.data.results ?? [],
  isFetching: state.listCountryInfoHubSubjects.isFetching,
  dashboardData: state.listCountryInfoHubDashboard.data,
  isDashboardFetching: state.listCountryInfoHubDashboard.isFetching,
});

type AppDispatch = Dispatch<UnknownAction>;

const dispatchAction = (dispatch: AppDispatch, action: unknown) =>
  dispatch(action as UnknownAction);

export const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => ({
  listCountryInfoHubSubjects: filters =>
    dispatchAction(dispatch, listCountryInfoHubSubjects(filters)),
  listCountryInfoHubDashboard: filters =>
    dispatchAction(dispatch, listCountryInfoHubDashboard(filters)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(SelectedCountryTable));
